import { DeleteOutlineRounded, SaveOutlined } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as UserReducer from "../../../../redux/users/users.reducers";
import { primary, secondary } from "../../../../themes/theme";
import DeleteItemDialog from "../../../../utils/common/delete-item-dialog/DeleteItemDialog";
import SecondarySearchBar from "../../../../utils/common/secondary-search-bar/SecondarySearchBar";
import CustomTooltip from "../../../../utils/CustomTooltip";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import ToggleSwitch from "../../components/toggle-switch/ToggleSwitch";
import { IUserView } from "../../models/IUserView";

const UserManagement: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userIdToDelete, setUserIdToDelete] = useState<string | undefined>("");

  // Get Users data from redux store
  const usersReduxState: UserReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[UserReducer.userFeatureKey];
    }
  );

  const { loading, users, user: currentUser } = usersReduxState;
  const { _id } = currentUser;
  const currentUserId = _id;

  const [adminStates, setAdminStates] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [superAdminStates, setSuperAdminStates] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    dispatch(UserActions.getAllUsersAction());
  }, []);

  useEffect(() => {
    if (users) {
      const adminStatus: { [key: string]: boolean } = {};
      const superAdminStatus: { [key: string]: boolean } = {};
      users.forEach((user) => {
        if (user._id) {
          adminStatus[user._id] = user.isAdmin ?? false;
          superAdminStatus[user._id] = user.isSuperAdmin ?? false;
        }
      });
      setAdminStates(adminStatus);
      setSuperAdminStates(superAdminStatus);
    }
  }, [users]);

  const handleDeleteUser = (userId: string | undefined) => {
    setDeleteDialogOpen(true);
    setUserIdToDelete(userId);
  };

  const handleDeleteConfirm = () => {
    if (userIdToDelete) {
      dispatch(UserActions.deleteUserAction({ userId: userIdToDelete })).then(
        (response: any) => {
          if (response && !response.error) {
            dispatch(UserActions.getUserDataAction());
            dispatch(UserActions.getAllUsersAction());
          }
        }
      );
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleUpdateUser = (user: IUserView) => {
    const userId = user._id;
    if (userId) {
      const updatedUser: Pick<IUserView, "isAdmin" | "isSuperAdmin"> = {
        isAdmin: adminStates[userId],
        isSuperAdmin: superAdminStates[userId],
      };

      // Ensure that if the user is a super admin, they are also an admin
      if (updatedUser.isSuperAdmin) {
        updatedUser.isAdmin = true;
      }

      dispatch(
        UserActions.updateUserRoleAction({
          user: updatedUser as IUserView,
          userId: userId,
        })
      ).then((response: any) => {
        if (response && !response.error) {
          dispatch(UserActions.getUserDataAction());
          dispatch(UserActions.getAllUsersAction());
        }
      });
    }
  };

  const handleAdminChange = (userId: string) => (event: any) => {
    setAdminStates((prevState) => ({
      ...prevState,
      [userId]: event.target.checked,
    }));
  };

  const handleSuperAdminChange = (userId: string) => (event: any) => {
    setSuperAdminStates((prevState) => ({
      ...prevState,
      [userId]: event.target.checked,
    }));
    if (event.target.checked) {
      setAdminStates((prevState) => ({
        ...prevState,
        [userId]: true,
      }));
    }
  };

  //Styles
  const cellStyle1 = {
    color: primary[800],
    fontWeight: "bold",
    fontFamily: "Ubuntu",
  };

  const cellStyle2 = {
    fontFamily: "Ubuntu",
    textTransform: "captilaize",
    Width: "100%",
  };

  return (
    <>
      <Box sx={{ mb: 2, pb: 2 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="User Management"
          description="Efficiently manage user roles, permissions, and profiles for streamlined access and control."
        />

        {/* Search Bar  */}
        <SecondarySearchBar />

        <Grid
          container
          sx={{ pl: { xs: 1, md: 9, lg: 10 }, pr: { xs: 1, md: 2 } }}
        >
          {users && users.length > 0 ? (
            <>
              <Grid item xs={12}>
                <Typography
                  sx={{ pb: 1, fontFamily: "Ubuntu", fontWeight: "500" }}
                >
                  Total Users : {users.length}
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ borderRadius: "10px" }}
                >
                  <Table>
                    <TableHead sx={{ background: secondary[300] }}>
                      <TableRow>
                        <TableCell sx={{ ...cellStyle1 }}>S.NO</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>User ID</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Image</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>User Name</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Email</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Is Admin</TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>
                          Is Super Admin
                        </TableCell>
                        <TableCell sx={{ ...cellStyle1 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user, index) => {
                        return (
                          <TableRow key={user._id}>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 50,
                                maxWidth: 60,
                                minWidth: "100%",
                              }}
                            >
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 100,
                                maxWidth: 100,
                                minWidth: "100%",
                              }}
                            >
                              {user._id
                                ?.substring(user._id.length - 5)
                                .toUpperCase()}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 100,
                                maxWidth: 100,
                                minWidth: "100%",
                              }}
                            >
                              <img
                                src={user.imageUrl}
                                alt=""
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "7px",
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 140,
                                maxWidth: 140,
                                minWidth: "100%",
                              }}
                            >
                              {user.username}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 180,
                                maxWidth: 180,
                                minWidth: "100%",
                              }}
                            >
                              {user.email.toLowerCase()}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                maxWidth: 120,
                                minWidth: "100%",
                              }}
                            >
                              <Box>
                                {user._id && (
                                  <ToggleSwitch
                                    checked={adminStates[user._id]}
                                    onChange={handleAdminChange(user._id)}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontFamily: "Ubuntu",
                                    fontWeight: "500",
                                  }}
                                >
                                  {adminStates[user._id ? user._id : ""]
                                    ? "YES"
                                    : "NO"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 120,
                                maxWidth: 120,
                                minWidth: "100%",
                              }}
                            >
                              <Box>
                                {user._id && (
                                  <ToggleSwitch
                                    checked={superAdminStates[user._id]}
                                    onChange={handleSuperAdminChange(user._id)}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    fontFamily: "Ubuntu",
                                    fontWeight: "500",
                                  }}
                                >
                                  {superAdminStates[user._id ? user._id : ""]
                                    ? "YES"
                                    : "NO"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyle2,
                                width: 160,
                                maxWidth: 160,
                                minWidth: "100%",
                              }}
                            >
                              <CustomTooltip title="Update" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "#0061AD",
                                    borderRadius: "7px",
                                    width: "30px",
                                    height: "30px",
                                    mr: 1,
                                    border:
                                      user._id === currentUserId
                                        ? " 2px solid lightgrey"
                                        : "2px solid #0061AD",
                                    "&:hover": {
                                      background: "#0061AD",
                                      color: "white",
                                    },
                                  }}
                                  disabled={user._id === currentUserId}
                                  onClick={() => handleUpdateUser(user)}
                                >
                                  <SaveOutlined />
                                </IconButton>
                              </CustomTooltip>
                              <CustomTooltip title="Delete" placement="top">
                                <IconButton
                                  size="small"
                                  sx={{
                                    background: "transparent",
                                    color: "orangered",
                                    borderRadius: "7px",
                                    width: "30px",
                                    height: "30px",
                                    border:
                                      user._id === currentUserId
                                        ? " 2px solid lightgrey"
                                        : "2px solid orangered",
                                    "&:hover": {
                                      background: "orangered",
                                      color: "white",
                                    },
                                  }}
                                  disabled={user._id === currentUserId}
                                  onClick={() => handleDeleteUser(user._id)}
                                >
                                  <DeleteOutlineRounded />
                                </IconButton>
                              </CustomTooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </>
          ) : (
            <>
              <Box>
                <Typography>No Users Found</Typography>
              </Box>
            </>
          )}
        </Grid>
      </Box>

      {/* Delete User Dialog  */}
      <DeleteItemDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="User Delete?"
        contentText="Are you sure you want to delete the User"
      />
    </>
  );
};

export default UserManagement;
